import { useState } from "react";
import { Instagram, Facebook, Twitter, Youtube, ArrowUpRight, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Vogue-inspired footer navigation. All external links go to vogue.com properties
// so the marketing site feels connected to the wider editorial world.
const LINK_COLUMNS = [
  {
    title: "Fashion",
    links: [
      { label: "Runway", href: "https://www.vogue.com/fashion-shows" },
      { label: "Street Style", href: "https://www.vogue.com/fashion/street-style" },
      { label: "Trends", href: "https://www.vogue.com/fashion/trends" },
      { label: "Designers", href: "https://www.vogue.com/fashion/designers" },
      { label: "Shopping", href: "https://www.vogue.com/shopping" },
    ],
  },
  {
    title: "Beauty",
    links: [
      { label: "Skin", href: "https://www.vogue.com/beauty/skin" },
      { label: "Hair", href: "https://www.vogue.com/beauty/hair" },
      { label: "Makeup", href: "https://www.vogue.com/beauty/makeup" },
      { label: "Wellness", href: "https://www.vogue.com/beauty/wellness" },
      { label: "Nails", href: "https://www.vogue.com/beauty/nails" },
    ],
  },
  {
    title: "Culture",
    links: [
      { label: "Celebrity Style", href: "https://www.vogue.com/celebrity-style" },
      { label: "Living", href: "https://www.vogue.com/living" },
      { label: "Arts", href: "https://www.vogue.com/culture/arts" },
      { label: "Music", href: "https://www.vogue.com/culture/music" },
      { label: "Weddings", href: "https://www.vogue.com/weddings" },
    ],
  },
  {
    title: "Vogue Editions",
    links: [
      { label: "Vogue US", href: "https://www.vogue.com" },
      { label: "British Vogue", href: "https://www.vogue.co.uk" },
      { label: "Vogue Paris", href: "https://www.vogue.fr" },
      { label: "Vogue Italia", href: "https://www.vogue.it" },
      { label: "Vogue Japan", href: "https://www.vogue.co.jp" },
      { label: "Vogue India", href: "https://www.vogue.in" },
    ],
  },
];

const READING_LIST = [
  {
    kicker: "Fashion Week",
    title: "Every runway moment from Paris SS26 you can't afford to miss",
    href: "https://www.vogue.com/fashion-shows/spring-2026-ready-to-wear",
  },
  {
    kicker: "Model File",
    title: "The new class of models rewriting the industry playbook",
    href: "https://www.vogue.com/tag/misc/models",
  },
  {
    kicker: "Editorial",
    title: "Inside the archives: covers that changed everything",
    href: "https://www.vogue.com/tag/misc/vogue-archive",
  },
  {
    kicker: "Diversity",
    title: "Casting director diaries — what agencies actually look for",
    href: "https://www.vogue.com/article/casting-director-interviews",
  },
];

export default function Footer({ onApply }) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const submitNewsletter = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid email.");
      return;
    }
    setSubscribed(true);
    toast.success("You're on the list.");
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a0a0a] text-[#ffffff] border-t border-white/10 overflow-hidden"
    >
      {/* Marquee CTA */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24">
        <div className="flex flex-col items-center text-center gap-6 pb-16">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#ffffff]/50">
            Ready when you are
          </p>
          <p className="font-script text-[#ffffff] text-5xl md:text-7xl leading-none -mb-2">
            your era starts now
          </p>
          <button
            data-testid="footer-apply-btn"
            onClick={onApply}
            className="font-display text-[16vw] md:text-[12vw] lg:text-[10rem] font-black uppercase leading-[0.85] tracking-tighter hover:text-white/70 transition-colors duration-500"
          >
            Get Scouted
          </button>
          <a
            href="mailto:hello@vogueverve.com"
            data-testid="footer-email"
            className="text-lg md:text-xl text-[#ffffff]/70 hover:text-[#ffffff] transition-colors underline underline-offset-8 decoration-[#ffffff]"
          >
            hello@vogueverve.com
          </a>
        </div>
      </div>

      {/* Editorial reading list */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 border-t border-white/10 py-16">
        <div className="flex items-end justify-between mb-10 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 mb-2">
              From the Editors
            </p>
            <h3 className="font-display text-3xl md:text-5xl font-light tracking-tight">
              Required <span className="italic">reading</span>.
            </h3>
          </div>
          <a
            href="https://www.vogue.com"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-more-stories"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors"
          >
            More stories <ArrowUpRight size={14} strokeWidth={2} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
          {READING_LIST.map((item, i) => (
            <a
              key={i}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              data-testid={`footer-story-${i}`}
              className="group bg-[#0a0a0a] hover:bg-[#141414] p-8 flex flex-col justify-between min-h-[220px] transition-colors"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40">
                {item.kicker}
              </p>
              <div>
                <p className="font-display text-xl md:text-2xl font-light leading-snug text-white group-hover:text-white transition-colors">
                  {item.title}
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold text-white/50 group-hover:text-white transition-colors">
                  Read <ArrowUpRight size={12} strokeWidth={2.4} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Sitemap + newsletter */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 border-t border-white/10 py-16 grid grid-cols-2 md:grid-cols-6 gap-10">
        {LINK_COLUMNS.map((col) => (
          <div key={col.title} className="col-span-1">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 mb-5">
              {col.title}
            </p>
            <ul className="space-y-3">
              {col.links.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="group inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                    <ArrowUpRight
                      size={11}
                      strokeWidth={2.2}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}

        {/* Newsletter — spans 2 cols on md+ */}
        <div className="col-span-2 md:col-span-2">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/40 mb-5">
            The Weekly Edit
          </p>
          <p className="font-display text-2xl md:text-3xl font-light leading-tight mb-5">
            Casting calls, model tips & editorial drops — every Thursday.
          </p>
          {subscribed ? (
            <p
              data-testid="newsletter-success"
              className="text-sm text-white/70 border border-white/25 rounded-full px-5 py-3 inline-flex items-center gap-2"
            >
              <Mail size={14} /> You&rsquo;re subscribed.
            </p>
          ) : (
            <form
              onSubmit={submitNewsletter}
              data-testid="newsletter-form"
              className="flex items-center gap-2 border-b border-white/25 focus-within:border-white transition-colors"
            >
              <Mail size={16} className="text-white/40" />
              <input
                data-testid="newsletter-email"
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:outline-none"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="text-xs uppercase tracking-[0.25em] font-bold text-white/60 hover:text-white transition-colors inline-flex items-center gap-1.5 py-3"
              >
                Join <ArrowRight size={12} strokeWidth={2.4} />
              </button>
            </form>
          )}
          <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mt-4 leading-relaxed">
            By subscribing you agree to our terms & privacy policy.
          </p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 border-t border-white/10 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
          <span className="font-display text-lg font-black tracking-tighter uppercase">
            Vogue<span className="text-[#ffffff]">Verve</span>
          </span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {[
              { label: "Privacy", href: "/legal#privacy" },
              { label: "Terms", href: "/legal#terms" },
              { label: "Editorial refs", href: "/legal#editorial" },
              { label: "Liability", href: "/legal#liability" },
              { label: "About", href: "/legal#about" },
              { label: "Support", href: "tel:+910000000000" },
            ].map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/50 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-5">
          <a href="https://www.instagram.com/voguemagazine" target="_blank" rel="noreferrer" data-testid="footer-instagram" className="text-white/60 hover:text-white transition-colors" aria-label="Instagram">
            <Instagram size={18} strokeWidth={1.5} />
          </a>
          <a href="https://www.facebook.com/vogue" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="Facebook">
            <Facebook size={18} strokeWidth={1.5} />
          </a>
          <a href="https://twitter.com/voguemagazine" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="Twitter">
            <Twitter size={18} strokeWidth={1.5} />
          </a>
          <a href="https://www.youtube.com/vogue" target="_blank" rel="noreferrer" className="text-white/60 hover:text-white transition-colors" aria-label="YouTube">
            <Youtube size={18} strokeWidth={1.5} />
          </a>
          <a href="/admin" data-testid="footer-admin-link" className="text-[10px] text-white/30 hover:text-white/70 uppercase tracking-[0.2em] ml-2">
            Admin
          </a>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] leading-relaxed">
          &copy; {new Date().getFullYear()} VOGUEVERVE Model Management. Editorial link-outs are inspired-by references only and not affiliated with Condé Nast or Vogue.
        </p>
      </div>
    </footer>
  );
}
